// Prompt 0457 T-1 — Modal de creacion/edicion de NivelPuesto.
// Extraido de AdminNivelesPuesto.tsx (640L) para respetar el umbral SOLID 500L
// y replicar el patron de paneles (puesto-panel/, rol-funcional-panel/).

import React, { useState } from 'react';
import {
  crearNivelPuesto,
  actualizarNivelPuesto,
  type NivelPuesto,
} from '../../../../../shared/services/api';
import { useI18n } from '../../../../../../i18n';
import { ActiveSwitch } from '../../../../../../shared/components/ActiveSwitch';

interface NivelModalProps {
  nivel: NivelPuesto | null;
  maxOrden: number;
  onClose: () => void;
  onSaved: () => void;
}

export function NivelModal({ nivel, maxOrden, onClose, onSaved }: NivelModalProps) {
  const { t } = useI18n();
  const adm = ((t.structure as Record<string, unknown>)?.admin as Record<string, unknown>) ?? {};
  const isEdit = nivel !== null;
  const [form, setForm] = useState<Record<string, string>>(() =>
    isEdit
      ? {
          codigo: nivel.codigo,
          nombre: nivel.nombre,
          nombreCorto: nivel.nombreCorto ?? '',
          orden: String(nivel.orden),
          color: nivel.color ?? '#6B7280',
          colorFondo: nivel.colorFondo ?? '#F3F4F6',
        }
      : {
          codigo: '',
          nombre: '',
          nombreCorto: '',
          orden: String(maxOrden + 1),
          color: '#6B7280',
          colorFondo: '#F3F4F6',
        },
  );
  const [activo, setActivo] = useState<boolean>(() => (isEdit ? (nivel.activo ?? true) : true));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.codigo?.trim() || !form.nombre?.trim()) {
      setError(
        ((adm as any).nivelModal as Record<string, string>)?.errorRequired ??
          'Codigo y nombre obligatorios',
      );
      return;
    }
    try {
      setSaving(true);
      const body = { ...form, orden: parseInt(form.orden) || 0, activo };
      if (isEdit) await actualizarNivelPuesto(nivel.id, body);
      else
        await crearNivelPuesto(
          body as {
            codigo: string;
            nombre: string;
            nombreCorto?: string;
            orden?: number;
            color?: string;
            colorFondo?: string;
            activo?: boolean;
          },
        );
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const set = (k: string, v: string) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError('');
  };

  const inp: React.CSSProperties = {
    padding: '8px 10px',
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    fontSize: 13,
    width: '100%',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: '#5A6171',
    marginBottom: 4,
    display: 'block',
  };
  const colorCircle = (c: string): React.CSSProperties => ({
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: c,
    border: '1px solid #D1D5DB',
    flexShrink: 0,
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          borderTop: '3px solid #1F3864',
          maxWidth: 500,
          width: '95%',
          padding: '24px 28px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#0E243E' }}>
          {isEdit
            ? ((adm as any).editNivel ?? 'Editar nivel')
            : ((adm as any).newLevel ?? 'Nuevo nivel')}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          <div>
            <label style={lbl}>{(adm as any).codigo ?? 'Codigo'} *</label>
            <input
              style={inp}
              value={form.codigo ?? ''}
              onChange={(e) => set('codigo', e.target.value)}
              disabled={isEdit}
            />
          </div>
          <div>
            <label style={lbl}>{(adm as any).nombre ?? 'Nombre'} *</label>
            <input
              style={inp}
              value={form.nombre ?? ''}
              onChange={(e) => set('nombre', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>
              {(adm as any).nombreCorto ?? 'Nombre corto'}{' '}
              <span style={{ fontSize: 10, color: '#94A3B8', fontWeight: 400 }}>(max 8)</span>
            </label>
            <input
              style={inp}
              maxLength={8}
              value={form.nombreCorto ?? ''}
              onChange={(e) => set('nombreCorto', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>{(adm as any).orden ?? 'Orden'}</label>
            <input
              type="number"
              style={inp}
              value={form.orden ?? ''}
              onChange={(e) => set('orden', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>{(adm as any).color ?? 'Color'}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="color"
                value={form.color ?? '#6B7280'}
                onChange={(e) => set('color', e.target.value)}
                style={{
                  width: 40,
                  height: 32,
                  border: '1px solid #D1D5DB',
                  borderRadius: 4,
                  cursor: 'pointer',
                  padding: 2,
                }}
              />
              <span style={colorCircle(form.color ?? '#6B7280')} />
              <span
                style={{
                  fontSize: 11,
                  color: '#6B7280',
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {form.color}
              </span>
            </div>
          </div>
          <div>
            <label style={lbl}>{(adm as any).colorFondo ?? 'Color de fondo'}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="color"
                value={form.colorFondo ?? '#F3F4F6'}
                onChange={(e) => set('colorFondo', e.target.value)}
                style={{
                  width: 40,
                  height: 32,
                  border: '1px solid #D1D5DB',
                  borderRadius: 4,
                  cursor: 'pointer',
                  padding: 2,
                }}
              />
              <span style={colorCircle(form.colorFondo ?? '#F3F4F6')} />
              <span
                style={{
                  fontSize: 11,
                  color: '#6B7280',
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {form.colorFondo}
              </span>
            </div>
          </div>
          <div>
            <label style={lbl}>{(adm as any).activo ?? 'Activo'}</label>
            <ActiveSwitch activo={activo} onChange={setActivo} />
          </div>
        </div>

        {/* Preview */}
        <div
          style={{
            marginTop: 12,
            padding: '8px 16px',
            background: '#F8F9FC',
            borderRadius: 6,
            border: '1px solid #E5E7EB',
          }}
        >
          <span style={{ fontSize: 11, color: '#5A6171' }}>Preview: </span>
          <span
            style={{
              display: 'inline-block',
              padding: '2px 10px',
              borderRadius: 10,
              fontSize: 12,
              fontWeight: 600,
              background: form.colorFondo,
              color: form.color,
            }}
          >
            {form.nombreCorto || form.nombre || 'Nivel'}
          </span>
          {form.nombreCorto && form.nombre && (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 600,
                background: form.colorFondo,
                color: form.color,
                marginLeft: 8,
              }}
            >
              {form.nombre}
            </span>
          )}
        </div>

        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              marginTop: 12,
              background: '#FEF2F2',
              color: '#DC2626',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              color: '#374151',
            }}
          >
            {(adm as any).cancel ?? 'Cancelar'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 24px',
              background: '#2F5496',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              fontFamily: 'inherit',
            }}
          >
            {saving ? 'Guardando...' : ((adm as any).save ?? 'Guardar')}
          </button>
        </div>
      </div>
    </div>
  );
}
